import { AvatarGroup } from "@chakra-ui/react";
import BaseImage from "@components/BaseImage";
import { DefiImg } from "@components/StorageImage";
import {
  DEFI_ADD_LIQUIDITY,
  // DEFI_SWAP,
  DEFI_WITHDRAW,
  FEATURED_OPPORTUNITIES,
  GENERAL_OPPORTUNITIES,
  VAULT_DEFI_ADD_LIQUIDITY,
} from "@constants/analytics";
import usePageData from "@hooks/usePageData";
import {
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@kelp-dao/kernel-design-system";
// import { ArrowsLeftRightIcon, InfoIcon } from "@phosphor-icons/react";
import Link from "next/link";
import React, { Fragment, useMemo } from "react";
import { HiArrowDown, HiArrowUp, HiOutlineArrowsUpDown } from "react-icons/hi2";
import { trackEvent } from "utils";
import { DeFiRow, PageData } from "./type";
import DefiRowTags from "./DefiRowTags";

interface ItemProps {
  row: DeFiRow;
  featuredDefi?: boolean;
}

export const RowButtons = ({ row, featuredDefi = false }: ItemProps) => {
  const showWithdraw = row.Provider?.toLowerCase() == "olive";
  const oliveWithdrawUrl = "https://www.olivenetwork.xyz/stake";
  return (
    <div className="flex gap-2 justify-start items-center">
      {!showWithdraw && row["Add Liquidity"] && (
        <Link href={row["Add Liquidity"]} target="_blank">
          <Button
            variant="outline"
            size="xsmall"
            onClick={() =>
              trackEvent(DEFI_ADD_LIQUIDITY, {
                defi_chain: row.Section,
                activity_type: row.Activity,
                defi_protocol: row.Provider,
                asset: row.Pair,
                source: featuredDefi
                  ? "top_opportunities"
                  : "general_opportunities",
              })
            }
          >
            Add Liquidity
          </Button>
        </Link>
      )}

      {/* {!showWithdraw && row.Swap && (
        <Link href={row.Swap} target="_blank">
          <Button
            variant="outline"
            size="xsmall"
            onClick={() =>
              trackEvent(DEFI_SWAP, {
                defi_chain: row.Section,
                activity_type: row.Activity,
                defi_protocol: row.Provider,
                asset: row.Pair,
                source: featuredDefi
                  ? FEATURED_OPPORTUNITIES
                  : GENERAL_OPPORTUNITIES,
              })
            }
            className="h-full"
          >
            <span className="items-center justify-center hidden lg:flex">
              <ArrowsLeftRightIcon size={16} />
            </span>
            <span className="items-center justify-center flex lg:hidden">
              Swap
            </span>
          </Button>
        </Link>
      )} */}

      {showWithdraw && (
        <Link href={oliveWithdrawUrl} target="_blank">
          <Button
            variant="outline"
            size="xsmall"
            onClick={() =>
              trackEvent(DEFI_WITHDRAW, {
                chain: row.Section,
                activity_type: row.Activity,
                defi_protocol: row.Provider,
                asset: row.Pair,
                source: featuredDefi
                  ? FEATURED_OPPORTUNITIES
                  : GENERAL_OPPORTUNITIES,
              })
            }
          >
            Withdraw
          </Button>
        </Link>
      )}
    </div>
  );
};

const DefiItemLG = ({ row, ...props }: ItemProps) => {
  const pageData = usePageData() as PageData | undefined;

  return (
    <TableRow {...props} className="" style={{ border: "none" }}>
      <TableCell className="pl-4 w-[20%] whitespace-nowrap py-[10px]">
        <div className="flex items-start gap-[0px]">
          <AvatarGroup max={2} spacing="-0.4rem">
            <DefiImg
              img={row["Token 1 Icon"]}
              style={{ width: 24, height: 24 }}
            />
            <DefiImg
              img={row["Token 2 Icon"]}
              style={{ width: 24, height: 24 }}
            />
          </AvatarGroup>
          <div className="flex flex-col gap-2">
            <Typography
              as="p"
              type="body"
              variant="small"
              className="text-text-primary pl-1"
            >
              {row.Pair || "-"}
            </Typography>
          </div>
        </div>
      </TableCell>
      <TableCell className="w-[18%] py-[10px]">
        <div className="flex items-center gap-2">
          <DefiImg img={row.Provider} style={{ width: 24, height: 24 }} />
          <Typography
            as="p"
            type="body"
            variant="small"
            className="text-text-primary"
          >
            {row.Provider || "-"}
          </Typography>
        </div>
      </TableCell>
      <TableCell className="w-[18%] text-text-primary py-[10px]">
        {pageData?.allTags && (row.Tags || row.TagWithImage) ? (
          <DefiRowTags
            allTags={pageData.allTags.data}
            tags={row.Tags}
            tagWithImage={row.TagWithImage}
            tagImagePath={row.TagImagePath}
          />
        ) : (
          "-"
        )}
      </TableCell>
      <TableCell className="w-[16%] py-[10px]">
        <Typography
          as="p"
          type="body"
          variant="small"
          className="text-text-primary"
        >
          {row.Activity}
        </Typography>
      </TableCell>
      <TableCell className="w-[14%] py-[10px]">
        <div className="flex gap-0.5">
          <Typography
            as="p"
            type="body"
            variant="small"
            className="text-text-primary"
          >
            {row.APY || "-"}
          </Typography>
        </div>
      </TableCell>
      <TableCell className="w-[14%] py-[10px] pr-4">
        <div className="flex items-center justify-between gap-3">
          <Typography
            as="p"
            type="body"
            variant="small"
            className="text-text-primary"
          >
            {row.TVL || "-"}
          </Typography>
          <RowButtons row={row} />
        </div>
      </TableCell>
    </TableRow>
  );
};

const DefiItemSM = ({ row }: ItemProps) => {
  const pageData = usePageData() as PageData | undefined;

  return (
    <Card
      className="w-full flex gap-[3px] p-4 rounded-xl"
      variant="non-bordered"
    >
      <div className="mt-1">
        <AvatarGroup size="sm" max={2} spacing="-0.4rem">
          <DefiImg
            img={row["Token 1 Icon"]}
            style={{ width: 24, height: 24 }}
          />
          <DefiImg
            img={row["Token 2 Icon"]}
            style={{ width: 24, height: 24 }}
          />
        </AvatarGroup>
      </div>
      <div className="flex flex-col gap-4 w-full">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 justify-between">
            <div className="flex gap-2 items-center">
              <div className="flex flex-col">
                <Typography type="body" variant="medium">
                  {row.Pair}
                </Typography>
              </div>
            </div>
            <div>
              <DefiImg img={row.Provider} size="sm" />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {pageData?.allTags && row.Tags ? (
              <DefiRowTags
                allTags={pageData.allTags.data}
                tags={row.Tags}
                tagWithImage={row.TagWithImage}
                tagImagePath={row.TagImagePath}
              />
            ) : null}
          </div>
        </div>

        <div className="flex items-center justify-between text-xs font-semibold font-sans">
          <div className="flex gap-4">
            <div>
              <span>
                <Typography type="body" variant="small">
                  {row.TVL || "-"}
                </Typography>
              </span>
              <span>
                <Typography
                  type="misc"
                  variant="overline"
                  className="text-text-secondary"
                >
                  TVL
                </Typography>
              </span>
            </div>
            <div>
              <span>
                <Typography type="body" variant="small">
                  {row.APY || "-"}
                </Typography>
              </span>
              <span>
                <Typography
                  type="misc"
                  variant="overline"
                  className="text-text-secondary"
                >
                  APY
                </Typography>
              </span>
            </div>
          </div>

          <RowButtons row={row} />
        </div>
      </div>
    </Card>
  );
};

interface BodyProps {
  rows: DeFiRow[];
}

// const DefiBodyMD = ({ rows }: BodyProps) => {
//   return (
//     <TableBody className="!py-0 border-none">
//       {rows.map((row, i) => (
//         <DefiItemLG row={row} key={`${row.Pair}_${i}`} />
//       ))}
//     </TableBody>
//   );
// };

const DefiBodySM = ({
  rows,
  ...props
}: BodyProps & React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className="flex flex-col gap-4 lg:hidden" {...props}>
      {rows.map((row, i) => (
        <Fragment key={`${row.Pair}_${i}`}>
          <DefiItemSM row={row} key={row.Pair} />
        </Fragment>
      ))}
    </div>
  );
};

interface Props {
  rows: DeFiRow[];
  sortConfig?: { key: any; direction: any };
  onSort?: (key: string) => void;
}

// Section to image mapping
const sectionImageMapping: { [key: string]: string } = {
  Ethereum: "/assets/chains/ethereum.svg",
  Arbitrum: "/assets/chains/arbitrum.svg",
  Base: "/assets/chains/base.svg",
  Optimism: "/assets/chains/optimism.svg",
  zkSync: "/assets/chains/zksync.svg",
  Linea: "/assets/chains/linea.svg",
  Blast: "/assets/chains/blast.svg",
  Mode: "/assets/chains/mode.svg",
  Polygon: "/assets/chains/polygon.svg",
  Avalanche: "/assets/chains/avalanche.png",
  Bera: "/assets/platforms/bera.jpeg",
  Scroll: "/assets/chains/scroll.svg",
  Unichain: "/assets/chains/unichain.svg",
  TAC: "/assets/chains/tac.svg",
  Tac: "/assets/chains/tac.svg",
  tac: "/assets/chains/tac.svg",
  // Add more mappings as needed
};

// Column header row component
export const ColumnHeaderRow = ({
  sortConfig,
  onSort,
}: {
  sortConfig?: { key: any; direction: any };
  onSort?: (key: string) => void;
}) => {
  const getIcon = (column: string) => {
    if (!sortConfig || sortConfig.key !== column)
      return <HiOutlineArrowsUpDown />;
    return sortConfig.direction === "ascending" ? (
      <HiArrowUp />
    ) : (
      <HiArrowDown />
    );
  };

  return (
    <TableRow className="bg-bg-secondary" style={{ border: "none" }}>
      <TableCell className="pl-0 w-[20%] whitespace-nowrap py-3 bg-bg-secondary border-t-bg-secondary">
        <Typography
          as="span"
          type="body"
          variant="xsmall"
          className="text-text-secondary font-normal pl-4"
        >
          Asset
        </Typography>
      </TableCell>
      <TableCell className="w-[18%] py-3 bg-bg-secondary">
        <Typography
          as="span"
          type="body"
          variant="xsmall"
          className="text-text-secondary font-normal"
        >
          Provider
        </Typography>
      </TableCell>
      <TableCell className="w-[18%] py-3 bg-bg-secondary">
        <Typography
          as="span"
          type="body"
          variant="xsmall"
          className="text-text-secondary font-normal"
        >
          Benefit
        </Typography>
      </TableCell>
      <TableCell className="w-[16%] py-3 bg-bg-secondary">
        <Typography
          as="span"
          type="body"
          variant="xsmall"
          className="text-text-secondary font-normal"
        >
          Activity
        </Typography>
      </TableCell>
      <TableCell className="w-[14%] py-3 bg-bg-secondary">
        <div
          className={`cursor-pointer flex gap-0.5 items-start ${
            onSort ? "hover:text-gray-800" : ""
          }`}
          onClick={() => onSort && onSort("APY")}
        >
          <Typography
            as="span"
            type="body"
            variant="xsmall"
            className="text-text-secondary font-normal"
          >
            APR
          </Typography>
          {onSort && getIcon("APY")}
        </div>
      </TableCell>
      <TableCell className="w-[14%] py-3 bg-bg-secondary">
        <div
          className={`cursor-pointer flex gap-0.5 items-start ${
            onSort ? "hover:text-gray-800" : ""
          }`}
          onClick={() => onSort && onSort("TVLinDigits")}
        >
          <Typography
            as="span"
            type="body"
            variant="xsmall"
            className="text-text-secondary font-normal"
          >
            TVL
          </Typography>
          {onSort && getIcon("TVLinDigits")}
        </div>
      </TableCell>
    </TableRow>
  );
};

// Header component for DefiListV2 - matches Figma design
export const DefiListHeader = ({
  sectionName = "Ethereum",
}: {
  sectionName?: string;
  sortConfig?: { key: any; direction: any };
  onSort?: (key: string) => void;
}) => {
  return (
    <div className="lg:bg-bg-secondary rounded-t-3xl">
      {/* Top header section with Ethereum icon and text */}
      <div className="flex items-center gap-2 px-4 py-3">
        <div className="w-6 h-6 flex items-center justify-center">
          {/* Dynamic blockchain logo based on section */}
          <BaseImage
            src={
              sectionImageMapping[sectionName] ||
              sectionImageMapping[sectionName.toLowerCase()] ||
              sectionImageMapping[sectionName.toUpperCase()] ||
              "/assets/chains/ethereum.svg"
            }
            alt={`${sectionName} logo`}
            width={20}
            height={20}
            onError={() => {
              console.log(`Failed to load image for section: ${sectionName}`);
              console.log(
                `Available mappings:`,
                Object.keys(sectionImageMapping)
              );
            }}
          />
        </div>
        <Typography
          as="h3"
          type="heading"
          variant="small"
          className="font-medium text-[16px] text-text-primary font-sans"
        >
          {sectionName}
        </Typography>
        {/* <InfoIcon size={16} className="text-gray-600" /> */}
      </div>
    </div>
  );
};

const DefiListV2 = ({ rows, sortConfig, onSort }: Props) => {
  const defaultSortConfig = { key: "APY", direction: "descending" };
  const currentSortConfig = sortConfig || defaultSortConfig;

  const sortedData = useMemo(() => {
    if (currentSortConfig.key) {
      return [...rows].sort((a, b) => {
        let v1: any = a[currentSortConfig.key];
        let v2: any = b[currentSortConfig.key];
        if (
          currentSortConfig.key == "APY" &&
          !["", "-"].includes(v1) &&
          !["", "-"].includes(v2)
        ) {
          v1 = parseFloat(v1) / 100.0;
          v2 = parseFloat(v2) / 100.0;
        }
        if (currentSortConfig.key == "TVLinDigits") {
          if (["", "-"].includes(v1)) v1 = "0";
          if (["", "-"].includes(v2)) v2 = "0";
          v1 = parseFloat(v1.replace(/[$,]+/g, ""));
          v2 = parseFloat(v2.replace(/[$,]+/g, ""));
        }
        if (v1 < v2) {
          return currentSortConfig.direction === "ascending" ? -1 : 1;
        }
        if (v1 > v2) {
          return currentSortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return rows;
  }, [rows, currentSortConfig]);

  return (
    <>
      <div className="hidden lg:block">
        <div className="bg-bg-card rounded-b-3xl pb-3 pt-0">
          <Table className="border-none">
            <TableBody className="!py-0 border-none">
              <ColumnHeaderRow sortConfig={currentSortConfig} onSort={onSort} />
              {sortedData.map((row, i) => (
                <DefiItemLG row={row} key={`${row.Pair}_${i}`} />
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      <DefiBodySM rows={rows} />
    </>
  );
};

export const DefiFeaturedItem = ({ row }: ItemProps) => {
  const pageData = usePageData() as PageData | undefined;

  return (
    <div className="w-full flex flex-col gap-6 bg-bg-card border-hidden rounded-3xl p-5 min-w-[300px]">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 justify-between">
          <div className="flex gap-2 items-center">
            <AvatarGroup size="sm" max={2} spacing="-0.4rem">
              <DefiImg img={row["Token 1 Icon"]} />
              <DefiImg img={row["Token 2 Icon"]} />
            </AvatarGroup>
            <div className="flex flex-col">
              <div className="flex">{row.Pair}</div>
            </div>
          </div>
          <div>
            <DefiImg img={row.Provider} size="sm" />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs font-semibold">
        <div className="flex flex-col">
          <Typography
            as="p"
            type="heading"
            variant="large"
            className="text-xl font-bold font-sans"
          >
            {row.TVL || "-"}
          </Typography>
          <div className="flex items-center gap-1">
            <Typography
              as="p"
              type="body"
              variant="small"
              className="text-xs font-medium"
            >
              TVL
            </Typography>
          </div>
        </div>
        <div className="flex flex-col">
          <Typography
            as="p"
            type="heading"
            variant="large"
            className="text-xl font-bold font-sans"
          >
            {row.APY || "-"}
          </Typography>
          <div className="flex items-center gap-1">
            <Typography
              as="p"
              type="body"
              variant="small"
              className="text-xs font-medium"
            >
              APY
            </Typography>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {pageData?.allTags &&
          (row.Activity || row.TagWithImage || row.Tags) && (
            <DefiRowTags
              allTags={pageData.allTags.data}
              tags={[row.Activity, row.Tags, row.TagWithImage].join(",")}
              tagWithImage={row.TagWithImage}
              tagImagePath={row.TagImagePath}
              featuredDefi
            />
          )}
      </div>

      <RowButtons row={row} featuredDefi />
    </div>
  );
};

const getPairText = (input: string) => {
  if (input.includes(" - ")) {
    const [part1, part2] = input.split(" - ").map((str) => str.trim());
    return part1 + "- " + part2;
  } else {
    return input.trim(); // "agETH" (or whatever the single word is)
  }
};

export const DefiVaultPartnerships = ({ row }: ItemProps) => {
  const pageData = usePageData() as PageData | undefined;

  return (
    <div className="w-full flex flex-col gap-1 bg-bg-canvas border-hidden rounded-3xl p-4 min-w-[200px] h-44 justify-between">
      <div className="flex flex-col gap-1">
        <div className="flex flex-col gap-1 justify-between">
          <div className="flex gap-1 items-center">
            <DefiImg img={row.Provider} size="xs" />
            <Typography as="p" type="body" variant="small" className="text-sm">
              {row.Provider}
            </Typography>
          </div>
          <div className="flex gap-0.5 items-center justify-end">
            <AvatarGroup size="xs" max={2} spacing="-0.4rem">
              <DefiImg img={row["Token 1 Icon"]} />
              <DefiImg img={row["Token 2 Icon"]} />
            </AvatarGroup>
            <div className="flex flex-col">
              <Typography
                as="p"
                type="body"
                variant="small"
                className="text-sm"
              >
                {getPairText(row.Pair)}
              </Typography>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="flex flex-col text-xs font-semibold">
          <div className="flex justify-between">
            <Typography
              as="p"
              type="body"
              variant="small"
              className="font-light"
            >
              TVL
            </Typography>

            <Typography as="p" type="body" variant="small">
              {row.TVL || "-"}
            </Typography>
          </div>
          <div className="flex justify-between">
            <Typography
              as="p"
              type="body"
              variant="small"
              className="font-light"
            >
              APY
            </Typography>

            <Typography as="p" type="body" variant="small">
              {row.APY || "-"}
            </Typography>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          {pageData?.allTags &&
            (row.Activity || row.TagWithImage || row.Tags) && (
              <DefiRowTags
                allTags={pageData.allTags.data}
                tags={[row.Activity, row.Tags, row.TagWithImage].join(",")}
                tagWithImage={row.TagWithImage}
                tagImagePath={row.TagImagePath}
                featuredDefi
              />
            )}
        </div>

        {row["Add Liquidity"] && (
          <Link href={row["Add Liquidity"]} target="_blank">
            <Button
              size="xsmall"
              variant="primary"
              onClick={() =>
                trackEvent(VAULT_DEFI_ADD_LIQUIDITY, {
                  chain: row.Section,
                  activity_type: row.Activity,
                  defi_protocol: row.Provider,
                  asset: row.Pair,
                })
              }
            >
              Deposit
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
};

export default DefiListV2;
